"use client"

import IconArrowRight from "@/app/(site)/_components/Atoms/Icons/IconArrowRight"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { clsx } from "@/src/utils/clsx"
import { type MenuItemTypes } from "@/src/utils/navigation/menuItems"
import { useEffect, useRef, type CSSProperties } from "react"

const DESKTOP_ARROW_CLASS = clsx(
    "h-12 w-12 shrink-0",
    "translate-x-[1px] opacity-0",
    "transition-[opacity,transform] duration-200 ease-out",
    "group-hover:translate-x-[6px] group-hover:opacity-100",
    "motion-reduce:transition-none"
)

type Variant = "desktop" | "mobile"

type MenuL3L4ListProps = {
    items: MenuItemTypes[]
    onNavigate: (item: MenuItemTypes) => void
    variant: Variant
    /**
     * Mobile only: draw a bottom divider. Set when another content section
     * follows this one. When this is the last section, the footer's top border
     * owns the divider instead, so this stays false to avoid a double border.
     */
    showBottomBorder?: boolean
}

const VARIANT_CLASSES = {
    desktop: {
        list: "desktop-nav-l3 flex flex-col px-24 pt-24",
        rowWithLvl4: "flex gap-12 items-start",
        rowWithoutLvl4: "flex",
        lvl3Link:
            "group relative max-w-[50%] flex-1 pt-3 pb-20 text-col-text-primary Trim_Mono_POC_Text_14_130_-5_500_uppercase before:absolute before:inset-x-0 before:top-1/2 before:-translate-y-1/2 before:h-[36px] before:content-['']",
        lvl3Span:
            "flex max-w-[50%] flex-1 items-center pt-3 pb-20 text-col-text-primary Trim_Mono_POC_Text_14_130_-5_500_uppercase",
        lvl4Wrapper: "flex-1",
        lvl4Container: "flex flex-col gap-4 pt-2 pb-24",
        lvl4Link:
            "group pt-1 relative flex items-center text-col-text-tertiary transition-colors duration-200 Trade_Gothic_Text_14_150_0_300 hover:text-col-text-primary before:absolute before:inset-x-0 before:top-1/2 before:-translate-y-1/2 before:h-[36px] before:content-['']",
        lvl4Span: "pt-3 block text-col-text-tertiary Trade_Gothic_Text_14_150_0_300",
        useMenuRole: true
    },
    mobile: {
        list: "flex flex-1 flex-col items-start px-12 pb-48 pt-12",
        rowWithLvl4: "flex w-full items-start gap-12 pb-20",
        rowWithoutLvl4: "w-full",
        lvl3Link:
            "flex flex-col items-start justify-center py-8 text-col-text-primary Trim_Mono_POC_Text_18_130_-3_500_uppercase",
        lvl3Span:
            "flex flex-col items-start justify-center py-8 text-col-text-primary Trim_Mono_POC_Text_18_130_-3_500_uppercase",
        lvl4Wrapper: "flex-1",
        lvl4Container: "flex flex-col gap-4 pt-6",
        lvl4Link:
            "flex items-center py-2 text-col-text-secondary Trade_Gothic_Text_14_150_0_300",
        lvl4Span:
            "flex items-center py-2 text-col-text-secondary Trade_Gothic_Text_14_150_0_300",
        useMenuRole: false
    }
} as const

// Desktop L3 link: the title can wrap to two lines (max-w-[50%]). Wrapping text
// fills the column width, so the box is shrunk to its longest rendered line and
// the arrow is positioned against that box (left-full = end of text, top-1/2 =
// the row break) — keeping the arrow hugging the text and vertically centred.
function DesktopL3Link({
    item,
    href,
    className,
    style,
    tabIndex,
    onNavigate
}: {
    item: MenuItemTypes
    href: string
    className: string
    style?: CSSProperties
    tabIndex?: number
    onNavigate: (item: MenuItemTypes) => void
}) {
    const textRef = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        const text = textRef.current
        const title = text?.firstChild
        const link = text?.parentElement
        if (!text || !title || !link) return

        const fitToLongestLine = () => {
            text.style.width = ""
            const range = document.createRange()
            range.selectNode(title)
            const longestLine = Array.from(range.getClientRects()).reduce(
                (widest, rect) => Math.max(widest, rect.width),
                0
            )
            text.style.width = `${Math.ceil(longestLine)}px`
        }

        fitToLongestLine()

        const observer = new ResizeObserver(fitToLongestLine)
        observer.observe(link)

        return () => observer.disconnect()
    }, [item.title])

    return (
        <DynamicLink
            href={href}
            prefetch={false}
            tabIndex={tabIndex}
            className={className}
            style={style}
            onClick={() => onNavigate(item)}
        >
            <span ref={textRef} className="relative block">
                {item.title}
                <IconArrowRight
                    className={clsx(
                        DESKTOP_ARROW_CLASS,
                        "absolute left-full top-1/2 -translate-y-1/2"
                    )}
                    ariaLabel=""
                />
            </span>
        </DynamicLink>
    )
}

export default function MenuL3L4List({
    items,
    onNavigate,
    variant,
    showBottomBorder
}: MenuL3L4ListProps) {
    if (items.length === 0) return null

    const styles = VARIANT_CLASSES[variant]
    const listRole = styles.useMenuRole ? "menu" : undefined
    const itemRole = styles.useMenuRole ? "menuitem" : undefined
    // Mobile: the two-container layout is decided per view, not per link. If any
    // item in this view has sublinks, every row uses the half-width layout so
    // they align; if none do, every row spans the full width.
    const viewHasLvl4 = items.some(item => item.menuItems.some(s => !s.hidden))
    // Desktop links are part of the panel's roving tabindex (single tab stop);
    // mobile links stay in the natural tab order within the focus trap.
    const linkTabIndex = variant === "desktop" ? -1 : undefined

    return (
        <ul
            role={listRole}
            className={clsx(
                styles.list,
                variant === "mobile" && showBottomBorder && "border-b border-b-col-border"
            )}
        >
            {items.map(lvl3 => {
                const lvl4Items = lvl3.menuItems.filter(s => !s.hidden)
                const hasLvl4 = lvl4Items.length > 0
                const l3ColorStyle = lvl3.textColor?.hex
                    ? { color: lvl3.textColor.hex }
                    : undefined
                // Mobile: cap the L3 at half-width when the view uses the
                // two-container layout; otherwise it spans the full width.
                const lvl3WidthClass =
                    variant === "mobile" &&
                    (viewHasLvl4 ? "max-w-[50%] flex-1" : "w-full")
                // Desktop keeps its per-item row layout; mobile follows the view.
                const useTwoColumnRow = variant === "mobile" ? viewHasLvl4 : hasLvl4
                return (
                    <li
                        key={lvl3._key}
                        role={itemRole}
                        className={
                            useTwoColumnRow ? styles.rowWithLvl4 : styles.rowWithoutLvl4
                        }
                    >
                        {lvl3.uri ? (
                            variant === "desktop" ? (
                                <DesktopL3Link
                                    item={lvl3}
                                    href={lvl3.uri}
                                    tabIndex={linkTabIndex}
                                    className={styles.lvl3Link}
                                    style={l3ColorStyle}
                                    onNavigate={onNavigate}
                                />
                            ) : (
                                <DynamicLink
                                    href={lvl3.uri}
                                    prefetch={false}
                                    tabIndex={linkTabIndex}
                                    className={clsx(styles.lvl3Link, lvl3WidthClass)}
                                    style={l3ColorStyle}
                                    onClick={() => onNavigate(lvl3)}
                                >
                                    {lvl3.title}
                                </DynamicLink>
                            )
                        ) : (
                            <span
                                className={clsx(styles.lvl3Span, lvl3WidthClass)}
                                style={l3ColorStyle}
                            >
                                {lvl3.title}
                            </span>
                        )}

                        {hasLvl4 && (
                            <div className={styles.lvl4Wrapper}>
                                <ul role={listRole} className={styles.lvl4Container}>
                                    {lvl4Items.map(lvl4 => {
                                        const lvl4ColorStyle = lvl4.textColor?.hex
                                            ? { color: lvl4.textColor.hex }
                                            : undefined
                                        return (
                                            <li key={lvl4._key} role={itemRole}>
                                                {lvl4.uri ? (
                                                    <DynamicLink
                                                        href={lvl4.uri}
                                                        prefetch={false}
                                                        tabIndex={linkTabIndex}
                                                        className={styles.lvl4Link}
                                                        style={lvl4ColorStyle}
                                                        onClick={() => onNavigate(lvl4)}
                                                    >
                                                        {variant === "desktop" ? (
                                                            <>
                                                                <span className="pt-1">
                                                                    {lvl4.title}
                                                                </span>
                                                                <IconArrowRight
                                                                    className={
                                                                        DESKTOP_ARROW_CLASS
                                                                    }
                                                                    ariaLabel=""
                                                                />
                                                            </>
                                                        ) : (
                                                            <span className="pt-2">
                                                                {lvl4.title}
                                                            </span>
                                                        )}
                                                    </DynamicLink>
                                                ) : (
                                                    <span
                                                        className={styles.lvl4Span}
                                                        style={lvl4ColorStyle}
                                                    >
                                                        {lvl4.title}
                                                    </span>
                                                )}
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>
                        )}
                    </li>
                )
            })}
        </ul>
    )
}
